import { Injectable } from '@angular/core';

/** Valores válidos de configId para el flujo OIDC (login externo vs interno). */
export const OIDC_CONFIG_IDS = ['externos', 'internos'] as const;
export type OidcConfigId = (typeof OIDC_CONFIG_IDS)[number];

export const OIDC_CONFIG_IDS_PATHS = {
  externos: 'login',
  internos: 'login-internos'
} as const;
export type OidcConfigIdPath = (typeof OIDC_CONFIG_IDS_PATHS)[keyof typeof OIDC_CONFIG_IDS_PATHS];

const STORAGE_KEY = 'oidc_pending_config_id';

/**
 * Guarda en localStorage el configId antes de redirigir al IdP.
 * El callback lo lee para ejecutar checkAuth con la config correcta cuando
 * solo se puede usar una única redirect URL.
 */
@Injectable({ providedIn: 'root' })
export class OidcLoginContextService {
  /**
   * Guarda el configId (externos | internos) antes de llamar a authorize().
   * Debe invocarse al entrar a /login o /login-interno.
   */
  setPendingConfigId(configId: string): void {
    if (this.isValidConfigId(configId)) {
      localStorage.setItem(STORAGE_KEY, configId);
    }
  }

  /**
   * Obtiene el configId guardado para usar en el callback.
   * Retorna undefined si no hay valor o no es válido (entonces se usa config por defecto).
   */
  getPendingConfigId(): OidcConfigId | undefined {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      if (value && this.isValidConfigId(value)) {
        return value as OidcConfigId;
      }
    } catch {
      // localStorage no disponible o bloqueado
    }
    return undefined;
  }

  getPendingConfigIdPath(): OidcConfigIdPath {
    const configId = this.getPendingConfigId();
    return configId ? OIDC_CONFIG_IDS_PATHS[configId] : OIDC_CONFIG_IDS_PATHS.externos;
  }

  private isValidConfigId(value: string): value is OidcConfigId {
    return OIDC_CONFIG_IDS.includes(value as OidcConfigId);
  }
}
